import {AUDIENCE} from '../../../../../utils/constants'

// Option fields the text/value Likert editor doesn't manage but MUST carry through unchanged.
// The editor rebuilds the whole options array on every edit; without preserving these, editing a
// Likert question that has emoji/image options silently wiped all of them.
const PRESERVED_OPTION_FIELDS = [
  'staffEmoji',
  'pupilEmoji',
  'parentEmoji',
  'staffImage',
  'pupilImage',
  'parentImage',
  'showStaffEmojiLabel',
  'showPupilEmojiLabel',
  'showParentEmojiLabel',
  'showStaffImageLabel',
  'showPupilImageLabel',
  'showParentImageLabel',
]

const pickPreserved = option => {
  const out = {}
  for (const field of PRESERVED_OPTION_FIELDS) {
    if (option?.[field] !== undefined) out[field] = option[field]
  }
  return out
}

// Pure helpers for how the Likert answer-choice editor decides what to show for a question.
// Kept separate from the component so the logic is unit-testable without rendering MUI.

// Decide which editor mode a Likert question opens in. A question's own stored options are
// the source of truth: if it already carries answer options, open the builder seeded from
// them rather than reconstructing a scale from the legacy `likertOption` tag — that tag is
// often stale on template/bank questions and would paint the wrong labels/values over the
// real options (the cause of the builder/preview mismatch). `likertOption`/`customLikertScaleId`
// still preselect their dropdowns and only replace the options on an explicit user change.
export const initialLikertSource = question => {
  if (question?.customLikertScaleId) return 'custom'
  if ((question?.multiOptionResponseAttributes?.length ?? 0) >= 2) return 'builder'
  if (question?.likertOption) return 'predefined'
  return 'builder'
}

// Each option stores a separate label per audience (staffText / parentText / pupilText). The
// editor must show the label for the audience being edited — the same field the live preview
// reads — because a parent/pupil survey often leaves the unused staff label as placeholder junk
// (e.g. "Anwer 1..5"). Read the audience's own field first, then fall back to any populated label
// so an empty field doesn't blank the row.
export const audienceOptionText = (option, audience) => {
  const own =
    audience === AUDIENCE.PARENT
      ? option.parentText
      : audience === AUDIENCE.STUDENT
        ? option.pupilText
        : option.staffText
  return own || option.staffText || option.parentText || option.pupilText || ''
}

// Seed the builder rows from a question's stored options for the audience being edited.
export const seedOptionsFromQuestion = (question, audience) => {
  const existing = (question?.multiOptionResponseAttributes ?? [])
    .slice()
    .sort((a, b) => a.order - b.order)
  if (existing.length < 2) {
    return [
      {text: '', value: 1},
      {text: '', value: 2},
    ]
  }
  return existing.map(o => ({
    ...pickPreserved(o),
    text: audienceOptionText(o, audience),
    value: o.numValue ?? o.value ?? 0,
  }))
}

// The score to persist for an option. Preserves a legitimate 0 (e.g. "Don't know = 0"); only
// falls back to the ordinal position when the value is blank or non-numeric. The old
// `Number(o.value) || i + 1` treated 0 as falsy, so "Don't know = 0" was silently saved as the
// option's position (e.g. 4) — corrupting the score and the follow-up value shown for it.
export const scoreForOption = (rawValue, index) => {
  const parsed = Number(rawValue)
  return rawValue != null && rawValue !== '' && Number.isFinite(parsed)
    ? parsed
    : index + 1
}

// Turn the editable rows into the question's multiOptionResponseAttributes shape.
export const toMultiOptions = options =>
  options.map((o, i) => {
    const value = scoreForOption(o.value, i)
    const text = (o.text ?? '').toString()
    return {
      ...pickPreserved(o),
      value,
      numValue: value,
      staffText: text,
      pupilText: text,
      parentText: text,
      order: i + 1,
    }
  })

// Move an option from one position to another (immutable). Used by the Likert editor's
// drag-to-reorder — the predefined-Likert editor dropped the reordering the non-Likert editor has.
export const moveOption = (options, from, to) => {
  const next = [...(options ?? [])]
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= next.length ||
    to >= next.length
  ) {
    return next
  }
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

// Build the patch that sets (or clears, when `unified` is null) an option's emoji for the audience
// being edited. Mirrors the non-Likert editor: the priority audience cascades to the ones below it
// (staff -> all, parent -> parent+pupil, student -> pupil); a non-priority audience sets only its own.
export const emojiPatch = (audience, isPriority, unified) => {
  const patch = {}
  if (isPriority) {
    if (audience === AUDIENCE.STAFF) {
      patch.staffEmoji = unified
      patch.parentEmoji = unified
      patch.pupilEmoji = unified
    } else if (audience === AUDIENCE.PARENT) {
      patch.parentEmoji = unified
      patch.pupilEmoji = unified
    } else {
      patch.pupilEmoji = unified
    }
  } else if (audience === AUDIENCE.STAFF) {
    patch.staffEmoji = unified
  } else if (audience === AUDIENCE.PARENT) {
    patch.parentEmoji = unified
  } else {
    patch.pupilEmoji = unified
  }
  return patch
}

// The emoji codepoint to display for an option in the audience being edited (falls back to any
// populated one). Returns null when the option has no emoji, so the editor shows nothing.
export const optionEmoji = (option, audience) => {
  const own =
    audience === AUDIENCE.PARENT
      ? option?.parentEmoji
      : audience === AUDIENCE.STUDENT
        ? option?.pupilEmoji
        : option?.staffEmoji
  return own || option?.staffEmoji || option?.parentEmoji || option?.pupilEmoji || null
}

// Default score for a newly-added option: the smallest positive integer not already in use, so a
// delete-then-add fills the gap instead of skipping or duplicating (the old code used
// `length + 1`, which left gaps and could collide with an existing value).
export const nextOptionValue = options => {
  const used = new Set((options ?? []).map(o => Number(o.value)))
  let next = 1
  while (used.has(next)) next++
  return next
}
